"use client";

import { useState, useEffect } from "react";
import { useNow } from "@/lib/hooks/useNow";
import { parseBerlinDate } from "@/lib/utils/berlinDate";
import { track } from "@vercel/analytics";
import { trackEvent } from "@/lib/analytics";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { de } from "date-fns/locale";
import { startOfDayISO } from "@/lib/utils/date";
import { storyblokEditable } from "@storyblok/react/rsc";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { EventCard } from "@/components/layout/EventCard";
import { GridContainer } from "@/components/layout/GridContainer";
import { RichTextField } from "@/components/typography/RichTextField";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { MemberLockIcon, LockedIcon } from "@/components/icons/MemberLockIcon";
import { useMembership } from "@/lib/hooks/useMembership";
import { CheckCircleFilled } from "@/components/icons/CheckCircleFilled";
import { useMySignups } from "@/lib/hooks/useMySignups";
import type { EventStoryblok, EventSignUpSectionStoryblok } from "@/component-types-sb";
import { eventTypeFromContent } from "@/lib/utils/eventType";
import {
  fetchStoryblokList,
  fetchEventsAndTravels,
} from "@/lib/storyblok/fetchList";

type EventSignUpSectionProps = {
  blok: EventSignUpSectionStoryblok;
  eventDate?: Date;
  eventCategory?: string;
  eventUuid?: string;
  eventTitle?: string;
  eventSlug?: string;
  eventMemberOnly?: boolean;
  eventOriginalDate?: Date;
  signUpLink?: string;
  signUpLabel?: string;
  onSignupClick?: () => void;
};

type NextEventData = {
  title: string;
  date: Date;
  eventType: string;
  memberOnly: boolean;
  ctaLink: string;
} | null;

const EventSignUpSection = ({ blok, eventDate, eventCategory, eventUuid, eventTitle, eventSlug, eventMemberOnly, eventOriginalDate, signUpLink, signUpLabel, onSignupClick }: EventSignUpSectionProps) => {
  const { isMember } = useMembership();
  const [nextEvent, setNextEvent] = useState<NextEventData>(null);
  const { isSignedUp, refresh: refreshSignups } = useMySignups();

  const today = useNow();
  const isBeforeEvent = today
    ? (eventDate ? eventDate >= new Date(today.getFullYear(), today.getMonth(), today.getDate()) : false)
    : true;
  const isAfterEvent = today ? !isBeforeEvent : false;

  const identityDate = eventOriginalDate || eventDate;
  const signedUp = eventUuid && identityDate
    ? isSignedUp(eventUuid, identityDate.toISOString())
    : false;

  // Check if user can join (1 hour before event)
  const [now, setNow] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    setNow(Date.now());
    if (!signedUp || !eventDate) return;
    const interval = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, [signedUp, eventDate]);

  const BERLIN_TZ = "Europe/Berlin";
  const canJoin = now !== null && eventDate ? (eventDate.getTime() - now <= 60 * 60 * 1000) : false;
  const joinOpenTime = eventDate
    ? format(toZonedTime(new Date(eventDate.getTime() - 60 * 60 * 1000), BERLIN_TZ), "dd.MM. HH:mm", { locale: de })
    : "";
  const eventStartTime = eventDate
    ? format(toZonedTime(eventDate, BERLIN_TZ), "HH:mm", { locale: de })
    : "";

  const handleCancelSignup = async () => {
    if (!eventUuid || !identityDate) return;
    const res = await fetch("/api/events/signup", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        storyblokEventUuid: eventUuid,
        eventDate: identityDate.toISOString(),
      }),
    });
    if (res.ok) {
      refreshSignups();
      trackEvent("event_signup_cancelled", {
        event_title: eventTitle,
        event_type: eventCategory,
        event_date: eventDate?.toISOString(),
        // Ohne die uuid liess sich eine Abmeldung keinem Event zuordnen —
        // die Haelfte aller Cancels war dadurch nicht auswertbar.
        storyblok_event_uuid: eventUuid,
      });
      track("Event signup cancelled", { event_title: eventTitle || "", event_type: eventCategory || "" });
    }
  };

  // Use event_type from blok if set, otherwise fall back to eventCategory from parent
  const eventType = blok.event_type || eventCategory || "";

  // Fetch next event if this one has passed
  useEffect(() => {
    if (!isAfterEvent || !eventType) {
      return;
    }

    const fetchNextEvent = async () => {
      const now = startOfDayISO();

      try {
        // This blok never sits on a trip (travel does not include it), but
        // an event page can point at the next trip. Trips are their own
        // content type; the info sessions stay events with `reiseInfo`.
        const stories =
          eventType === "reisen"
            ? await fetchEventsAndTravels({
                filter_query: { date: { gt_date: now } },
                eventOnlyFilter: { category: { in: "reiseInfo" } },
                sort_by: "content.date:asc",
                per_page: 1,
                exact: true,
              })
            : await fetchStoryblokList({
                content_type: "event",
                filter_query: {
                  date: { gt_date: now },
                  category: { in: eventType },
                },
                sort_by: "content.date:asc",
                per_page: 1,
              });

        if (stories && stories.length > 0) {
          const nextEventStory = stories[0];
          const content = nextEventStory.content as EventStoryblok;

          setNextEvent({
            title: content.title,
            date: parseBerlinDate(content.date),
            eventType: eventTypeFromContent(content),
            memberOnly: (content as { member_access?: string }).member_access === "member_only",
            ctaLink: `/${nextEventStory.full_slug}`,
          });
        }
      } catch (error) {
        console.error("Error fetching next event:", error);
      }
    };

    fetchNextEvent();
  }, [isAfterEvent, eventType]);

  // Get sign up link from event
  const signUpUrl = signUpLink || "#";
  const buttonLabel = signUpLabel || "Jetzt anmelden";

  return (
    <GridContainer {...storyblokEditable(blok)} id="event-signup-section" className="py-12 !px-0 md:!px-8 scroll-mt-32">
      <div className="col-span-12 md:col-span-8 md:col-start-3 lg:col-span-6 lg:col-start-4 flex flex-col items-center">
        <div className="w-full px-4 md:px-0">
          <section className="bg-sky-75 rounded-3xl p-6 flex flex-col gap-6 items-center w-full">
            {isBeforeEvent && signedUp && (
              <>
                <CheckCircleFilled className="size-8 text-black" />
                <div className="flex flex-col items-center gap-1 text-center">
                  <h2 className="text-headline-xl text-black">
                    {canJoin ? "Es geht los" : "Du bist für das Event angemeldet"}
                  </h2>
                  {!canJoin && (
                    <p className="text-copy text-black max-w-[80%]">
                      Die Teilnahme ist ab {joinOpenTime} Uhr möglich.<br />Das Event startet um {eventStartTime}.
                    </p>
                  )}
                </div>
                {signUpLink && canJoin && (
                  <>
                    <Button
                      variant="sun"
                      className="w-full md:w-auto"
                      asChild
                      onClick={() => {
                        trackEvent("event_attended", {
                          event_title: eventTitle,
                          event_type: eventCategory,
                          event_date: eventDate?.toISOString(),
                          event_slug: eventSlug,
                          storyblok_event_uuid: eventUuid,
                          action: "join",
                        });
                        track("Event attended", { event_title: eventTitle || "", event_type: eventCategory || "" });
                      }}
                    >
                      <a href={signUpLink} target="_blank" rel="noopener noreferrer">
                        Jetzt teilnehmen
                        <ArrowRight />
                      </a>
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(signUpLink);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                        trackEvent("event_attended", {
                          event_title: eventTitle,
                          event_type: eventCategory,
                          event_date: eventDate?.toISOString(),
                          event_slug: eventSlug,
                          storyblok_event_uuid: eventUuid,
                          action: "link_copied",
                        });
                        track("Event attended", { event_title: eventTitle || "", event_type: eventCategory || "" });
                      }}
                      className="text-copy text-black hover:underline transition-opacity"
                    >
                      {copied ? "Link kopiert!" : "Link kopieren"}
                    </button>
                  </>
                )}
                {signUpLink && !canJoin && (
                  <>
                    <Button variant="black" className="w-full md:w-auto opacity-30 text-sky-50" disabled>
                      Jetzt teilnehmen
                      <ArrowRight />
                    </Button>
                    <button
                      type="button"
                      onClick={handleCancelSignup}
                      className="text-copy text-black hover:underline transition-opacity"
                    >
                      Abmelden
                    </button>
                  </>
                )}
                {!signUpLink && (
                  <button
                    type="button"
                    onClick={handleCancelSignup}
                    className="text-copy text-black hover:underline transition-opacity"
                  >
                    Abmelden
                  </button>
                )}
              </>
            )}

            {isBeforeEvent && !signedUp && (
              <>
                <h2 className="text-headline-xl text-black text-center">
                  {blok.before_event_headline || "Zum Event anmelden"}
                </h2>
                {blok.before_event_copy ? (
                  <RichTextField value={blok.before_event_copy} className="text-copy text-black text-center" />
                ) : (
                  <p className="text-copy text-black text-center">
                    {eventMemberOnly
                      ? "Dieses Event ist exklusiv für Dialetics-Members. Erstelle dir jetzt einen Account."
                      : eventCategory === "diaTalk"
                        ? "DiaTalks sind kostenlos für alle. Um dich anzumelden brauchst du einen Account bei Dialetics."
                        : "Dieses Event ist kostenlos für alle. Um dich anzumelden brauchst du einen Account bei Dialetics."}
                  </p>
                )}
                {/* Farbe nach Zugang, wie auf den Karten: schwarz bei
                    kostenlosen Events, gelb bei Member-Only. Das Schloss
                    erscheint nur, wenn der Zugang fehlt. */}
                {onSignupClick ? (
                  <Button
                    variant={eventMemberOnly ? "sun" : "black"}
                    className="w-full md:w-auto"
                    onClick={onSignupClick}
                  >
                    {buttonLabel}
                    {eventMemberOnly && !isMember ? <LockedIcon className="size-4 text-black shrink-0" /> : <ArrowRight />}
                  </Button>
                ) : (
                  <Button
                    variant={eventMemberOnly ? "sun" : "black"}
                    className="w-full md:w-auto"
                    asChild
                  >
                    <Link href={signUpUrl}>
                      {buttonLabel}
                      {eventMemberOnly && !isMember ? <LockedIcon className="size-4 text-black shrink-0" /> : <ArrowRight />}
                    </Link>
                  </Button>
                )}
              </>
            )}

            {isAfterEvent && (
              <>
                <h2 className="text-headline-xl text-black text-center">
                  {blok.after_event_headline || "Das Event hat bereits stattgefunden."}
                </h2>
                {nextEvent && (
                  <>
                    {blok.after_event_copy && (
                      <RichTextField value={blok.after_event_copy} className="text-copy text-black text-center" />
                    )}
                    <EventCard
                      variant="densed"
                      eventType={nextEvent.eventType}
                      memberOnly={nextEvent.memberOnly}
                      date={nextEvent.date}
                      title={nextEvent.title}
                      ctaLink={nextEvent.ctaLink}
                      className="max-w-none"
                    />
                  </>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </GridContainer>
  );
};

export default EventSignUpSection;
